import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area,
} from 'recharts';
import { Activity, TrendingDown, Sparkles } from 'lucide-react';
import PatientIdentityHeader from '../components/PatientIdentityHeader';

/**
 * Patient progress page — graphs VAS pain over time, outcome-measure scores,
 * and recent objective-assessment timestamps. Visible to the patient (for self)
 * AND to staff (for any patient via /admin/patients/{id}/progress).
 */
export default function PatientProgress() {
  const { patient_id } = useParams();
  const { user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'clinician';
  const targetId = patient_id || user?.user_id;
  const [data, setData] = useState(null);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    api.get(`/patients/${targetId}/progress`).then(r => setData(r.data));
    if (isStaff) {
      api.get(`/patients/${targetId}`).then(r => setPatient(r.data)).catch(() => {});
    } else {
      api.get('/patients/me').then(r => setPatient(r.data)).catch(() => {});
    }
  }, [targetId, isStaff]);

  if (!data) return <div className="p-8 text-fg-muted">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="patient-progress-page">
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">RECOVERY</div>
      <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Progress over time</h1>
      <p className="text-fg-muted mt-2 mb-6">Subjective pain (VAS), outcome measures and objective findings — visualised across visits.</p>

      <PatientIdentityHeader patient={patient}/>

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <Tile icon={TrendingDown}
              label="Latest VAS pain"
              v={data.vas.length ? `${data.vas[data.vas.length-1].score}/10` : '—'}
              sub={data.vas.length > 1 ? `was ${data.vas[0].score}/10 at start` : 'first reading'}
              testid="tile-vas"/>
        <Tile icon={Activity}
              label="Objective check-ins"
              v={data.objective_history.length}
              sub="snapshots saved"
              testid="tile-obj"/>
        <Tile icon={Sparkles}
              label="Outcome series"
              v={data.outcomes.length}
              sub={data.outcomes.map(o=>o.name).join(' · ') || '—'}
              testid="tile-outcomes"/>
      </div>

      {data.vas.length >= 2 && (
        <div className="bento-card mb-4" data-testid="chart-vas">
          <h3 className="font-heading font-semibold mb-3">VAS pain · subjective</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.vas}>
                <defs>
                  <linearGradient id="vasGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B83A3A" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#B83A3A" stopOpacity={0.0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => (d||'').slice(0,10)}/>
                <YAxis domain={[0, 10]} tick={{ fontSize: 11 }}/>
                <Tooltip />
                <Area type="monotone" dataKey="score" stroke="#B83A3A" strokeWidth={2} fillOpacity={1} fill="url(#vasGrad)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {data.outcomes.map(out => (
        <div key={out.name} className="bento-card mb-4" data-testid={`chart-out-${out.name}`}>
          <h3 className="font-heading font-semibold mb-3">{out.name} score</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={out.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => (d||'').slice(0,10)}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Line type="monotone" dataKey="score" stroke="#003D80" strokeWidth={2} name="raw score"/>
                <Line type="monotone" dataKey="pct" stroke="#2A5C4E" strokeWidth={2} name="% disability"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ))}

      {(!data.vas.length && !data.outcomes.length && !data.objective_history.length) && (
        <div className="surface border border-soft rounded-lg p-8 text-center">
          <Activity className="w-10 h-10 text-fg-muted mx-auto"/>
          <p className="text-sm text-fg-muted mt-3">Nothing to chart yet — complete the intake and a few outcome measures to see progress.</p>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, v, sub, testid }) {
  return (
    <div className="surface border border-soft rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-fg-muted mb-2">
        <Icon className="w-3.5 h-3.5 text-brand"/> {label}
      </div>
      <div className="text-2xl font-heading font-bold text-fg">{v}</div>
      {sub && <div className="text-[11px] text-fg-muted mt-1 truncate">{sub}</div>}
    </div>
  );
}
